/**
 * Proposal commands — sherwood proposal <subcommand>
 *
 * Manages the full proposal lifecycle: create, list, show, vote,
 * execute, settle, cancel, simulate.
 */

import { Command } from "commander";
import type { Address, Hex } from "viem";
import { isAddress, encodeFunctionData } from "viem";
import { getVaultOwner, getAssetAddress, getAgentFeeBps } from "../lib/vault.js";
import { ERC20_ABI } from "../lib/abis.js";
import chalk from "chalk";
import ora from "ora";
import { readFileSync } from "node:fs";
import {
  getAccount,
  formatContractError,
  getPublicClient,
  writeContractWithRetry,
  waitForReceipt,
} from "../lib/client.js";
import { getChain, getExplorerUrl, getNetwork } from "../lib/network.js";
import { SHERWOOD } from "../lib/addresses.js";
import { isCalldataOnly, emitCalldata } from "../lib/calldata.js";
import { encodeVote, encodeExecute, encodeSettle, encodeCancel, encodeUnstick, encodeEmergencySettle, encodePropose, type ChainId } from "@sherwoodagent/sdk";
import { uploadMetadata } from "../lib/ipfs.js";
import type { FundMetadata } from "../lib/ipfs.js";
import { fetchMetadata } from "../lib/ipfs.js";
import {
  getGovernorOf,
  getGovernorParams,
  getProposal,
  getProposalState,
  proposalCount,
  propose,
  vote,
  executeProposal,
  settleProposal,
  emergencySettle,
  getExecuteCalls,
  getSettlementCalls,
  cancelProposal,
  emergencyCancel,
  getVoteWeight,
  hasVoted,
  getCapitalSnapshot,
  getActiveProposal,
  parseDuration,
  evaluateExecuteGate,
  classifyReview,
  PROPOSAL_STATES,
  PROPOSAL_STATE,
  VOTE_TYPE,
} from "../lib/governor.js";
import type { BatchCall } from "../lib/governor.js";
import { formatDurationShort as formatDuration, formatShares, formatUSDC, parseBigIntArg } from "../lib/format.js";
import { simulateBatchCalls, printSimulationResult, sendSimulationAlert } from "../lib/simulate.js";
import type { RiskContext } from "../lib/simulate.js";

const G = chalk.green;
const W = chalk.white;
const DIM = chalk.gray;
const BOLD = chalk.white.bold;
const LABEL = chalk.green.bold;
const SEP = () => console.log(DIM("─".repeat(60)));

function formatTimestamp(ts: bigint): string {
  if (ts === 0n) return "—";
  return new Date(Number(ts) * 1000).toLocaleString();
}

/**
 * Validate a required `--vault` option. Per-vault governor: proposal IDs
 * are scoped to a vault's governor, so every id-keyed subcommand needs the
 * vault to resolve the right governor.
 */
function parseVaultOption(raw: unknown, spinner?: { fail: (msg: string) => unknown }): Address {
  const vault = String(raw);
  if (!isAddress(vault)) {
    if (spinner) spinner.fail(`Invalid vault address: ${vault}`);
    else console.error(chalk.red(`Invalid vault address: ${vault}`));
    process.exit(1);
  }
  return vault as Address;
}

function parseCallsFile(path: string): BatchCall[] {
  const raw = readFileSync(path, "utf-8");
  const parsed = JSON.parse(raw) as { target: string; data: string; value: string }[];
  return parsed.map((c) => ({
    target: c.target as Address,
    data: c.data as Hex,
    value: BigInt(c.value || "0"),
  }));
}

// Minimal GuardianRegistry ABI fragment for the resolve-reviews keeper.
// `resolveReview` is permissionless + state-changing (triggers the
// approver slash on a block-quorum); `getReviewState` is the read used
// to find resolvable candidates. Per-vault governor: reviews are keyed
// by (governor, proposalId) — proposal IDs are only unique per governor.
const GUARDIAN_REGISTRY_ABI = [
  {
    type: "function",
    name: "resolveReview",
    inputs: [{ name: "governor", type: "address" }, { name: "proposalId", type: "uint256" }],
    outputs: [{ type: "bool" }],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "getReviewState",
    inputs: [{ name: "governor", type: "address" }, { name: "proposalId", type: "uint256" }],
    outputs: [
      { name: "opened", type: "bool" },
      { name: "resolved", type: "bool" },
      { name: "blocked", type: "bool" },
      { name: "cohortTooSmall", type: "bool" },
    ],
    stateMutability: "view",
  },
] as const;

function getRegistryAddress(): Address {
  const addr = SHERWOOD().GUARDIAN_REGISTRY;
  if (addr === "0x0000000000000000000000000000000000000000") {
    console.error(
      chalk.red(
        `GUARDIAN_REGISTRY is not deployed on ${getNetwork()}. Update cli/src/lib/addresses.ts after deploy.`,
      ),
    );
    process.exit(1);
  }
  return addr;
}

export function registerProposalCommands(program: Command): void {
  const proposal = program.command("proposal").description("Governance proposals — create, vote, execute, settle");

  // ── proposal create ──

  proposal
    .command("create")
    .description("Submit a strategy proposal")
    .requiredOption("--vault <address>", "Vault address the proposal targets")
    .requiredOption("--name <name>", "Strategy name")
    .requiredOption("--description <text>", "Strategy rationale and risk summary")
    .requiredOption("--duration <duration>", "Strategy duration (e.g. 7d, 24h, 3600)")
    .requiredOption("--execute-calls <path>", "Path to JSON file with execute Call[] array")
    .requiredOption("--settle-calls <path>", "Path to JSON file with settlement Call[] array")
    .option("--strategy <address>", "Live-NAV strategy clone address (default: none — queue-only)")
    .option("--metadata-uri <uri>", "Override — skip IPFS upload and use this URI directly")
    .option("--simulate", "Simulate calls via Tenderly before submitting")
    .option("--force", "Submit even if simulation fails (requires --simulate)")
    .action(async (opts) => {
      try {
        const vault = opts.vault as Address;
        if (!isAddress(vault)) {
          console.error(chalk.red(`Invalid vault address: ${opts.vault}`));
          process.exit(1);
        }

        const strategyDuration = parseDuration(opts.duration);
        const executeCalls = parseCallsFile(opts.executeCalls);
        const settleCalls = parseCallsFile(opts.settleCalls);

        // Live-NAV strategy clone (optional). Zero address = queue-only. The
        // strategy-template `propose --write-calls` flow prints the clone
        // address to pass here for an external-signer submission.
        const ZERO_STRATEGY = "0x0000000000000000000000000000000000000000" as Address;
        let strategy: Address = ZERO_STRATEGY;
        if (opts.strategy) {
          if (!isAddress(opts.strategy)) {
            console.error(chalk.red(`Invalid strategy address: ${opts.strategy}`));
            process.exit(1);
          }
          strategy = opts.strategy as Address;
        }

        // --calldata-only: emit propose calldata. IPFS metadata upload needs a
        // signing wallet, so a metadata URI must be supplied explicitly here.
        if (isCalldataOnly()) {
          if (!opts.metadataUri) {
            console.error(
              chalk.red(
                "--calldata-only requires --metadata-uri (IPFS metadata upload needs a signing wallet)",
              ),
            );
            process.exit(1);
          }
          try {
            emitCalldata(
              encodePropose(
                {
                  vault,
                  governor: await getGovernorOf(vault),
                  strategy,
                  metadataURI: opts.metadataUri,
                  strategyDuration,
                  executeCalls,
                  settlementCalls: settleCalls,
                },
                getChain().id as ChainId,
              ),
            );
          } catch (err) {
            console.error(chalk.red(err instanceof Error ? err.message : String(err)));
            process.exit(1);
          }
          return;
        }

        // ── Pin metadata ──
        let metadataURI = opts.metadataUri || "";

        if (!metadataURI) {
          const spinner = ora({ text: W("Uploading metadata to IPFS..."), color: "green" }).start();
          try {
            const account = getAccount();
            const metadata: FundMetadata = {
              schema: "sherwood/proposal/v1",
              name: opts.name,
              description: opts.description,
              chain: getNetwork(),
              strategies: [],
              terms: {},
              links: {},
            };
            // Attach proposal-specific fields
            const proposalMeta = {
              ...metadata,
              proposer: account.address,
              vault,
              strategyDuration: Number(strategyDuration),
              createdAt: Math.floor(Date.now() / 1000),
            };
            // Use the existing upload function — it accepts FundMetadata shape
            metadataURI = await uploadMetadata(proposalMeta as unknown as FundMetadata);
            spinner.succeed(G(`Metadata pinned: ${DIM(metadataURI)}`));
          } catch (err) {
            spinner.warn(chalk.yellow("IPFS upload failed — using inline metadata"));
            const json = JSON.stringify({ name: opts.name, description: opts.description, vault });
            metadataURI = `data:application/json;base64,${Buffer.from(json).toString("base64")}`;
          }
        }

        // Read the vault's agent fee once — shown in the confirmation below and
        // reused for the simulate risk context (M5: one read, no TOCTOU drift).
        // Governor params (only used by --simulate's bounds checks) are fetched
        // concurrently so the simulate path costs one fewer round-trip; a params
        // failure stays non-fatal via catch→null, and non-simulate skips it.
        const [vaultAgentFeeBps, preFetchedGovParams] = await Promise.all([
          getAgentFeeBps(vault),
          opts.simulate ? getGovernorParams(vault).catch(() => null) : Promise.resolve(null),
        ]);

        // ── Summary ──
        console.log();
        console.log(LABEL("  ◆ Proposal Summary"));
        SEP();
        console.log(W(`  Name:             ${BOLD(opts.name)}`));
        console.log(W(`  Vault:            ${G(vault)}`));
        console.log(W(`  Agent Fee:        ${Number(vaultAgentFeeBps) / 100}% ${DIM("(vault — snapshotted on-chain at propose)")}`));
        console.log(W(`  Duration:         ${formatDuration(strategyDuration)}`));
        console.log(W(`  Calls:            ${executeCalls.length} execute + ${settleCalls.length} settle`));
        console.log(W(`  Metadata:         ${DIM(metadataURI.length > 50 ? metadataURI.slice(0, 50) + "..." : metadataURI)}`));
        SEP();

        // ── Simulate (optional) ──
        if (opts.simulate) {
          // Build risk context from the proposal parameters. The agent fee is
          // read from the vault here — it's the value that will be snapshotted
          // onto the proposal at propose time.
          let createRiskContext: RiskContext = {
            vault,
            strategyDuration,
            performanceFeeBps: vaultAgentFeeBps,
          };
          // Params were fetched in parallel with the fee read above; null means
          // that read failed → skip the (non-fatal) parameter bounds checks.
          if (preFetchedGovParams) {
            createRiskContext = { ...createRiskContext, governorParams: preFetchedGovParams };
          }

          const simSpinner = ora({ text: W("Simulating execute calls..."), color: "green" }).start();
          const execResult = await simulateBatchCalls(vault, executeCalls, "execute", createRiskContext);
          simSpinner.stop();
          printSimulationResult(execResult, "execute");

          if (settleCalls.length > 0) {
            const settleSpinner = ora({ text: W("Simulating settlement calls..."), color: "green" }).start();
            const settleResult = await simulateBatchCalls(vault, settleCalls, "settlement", createRiskContext);
            settleSpinner.stop();
            printSimulationResult(settleResult, "settlement");

            if (!settleResult.success && !opts.force) {
              console.error(chalk.red("\n  ✖ Settlement simulation failed. Use --force to submit anyway."));
              process.exit(1);
            }
          }

          if (!execResult.success && !opts.force) {
            console.error(chalk.red("\n  ✖ Execution simulation failed. Use --force to submit anyway."));
            process.exit(1);
          }

          // Block on critical risks
          const hasCriticalRisks = execResult.risks.some((r) => r.level === "critical");
          if (hasCriticalRisks && !opts.force) {
            console.error(chalk.red("\n  ✖ Critical risks detected. Use --force to submit anyway."));
            process.exit(1);
          }
        }

        // ── Submit ──
        // Strategy address: zero for queue-only behavior, or the `--strategy`
        // clone for live NAV. The `strategy-template propose` command resolves
        // a freshly-cloned strategy address you can pass via `--strategy`.
        const spinner = ora({ text: W("Submitting proposal..."), color: "green" }).start();
        const result = await propose(
          vault,
          strategy,
          metadataURI,
          strategyDuration,
          executeCalls,
          settleCalls,
        );
        spinner.succeed(G("Proposal submitted"));

        console.log();
        console.log(LABEL("  ◆ Proposal Created"));
        SEP();
        console.log(W(`  Proposal ID:  ${G(`#${result.proposalId}`)}`));
        console.log(W(`  Tx:           ${DIM(getExplorerUrl(result.hash))}`));
        SEP();
        console.log();
      } catch (err) {
        console.error(chalk.red(`\n  ✖ ${formatContractError(err)}`));
        process.exit(1);
      }
    });

  // ── proposal list ──

  proposal
    .command("list")
    .description("List a vault's proposals (per-vault governor — IDs are scoped to the vault)")
    .requiredOption("--vault <address>", "Vault whose proposals to list")
    .option("--state <filter>", "Filter by state: draft, pending, approved, executed, settled, all", "all")
    .action(async (opts) => {
      const spinner = ora("Loading proposals...").start();
      try {
        const vault = parseVaultOption(opts.vault, spinner);
        const count = await proposalCount(vault);
        if (count === 0n) {
          spinner.stop();
          console.log(DIM("\n  No proposals found.\n"));
          return;
        }

        const stateFilter = opts.state.toLowerCase();
        const stateIndex = PROPOSAL_STATES.findIndex((s) => s.toLowerCase() === stateFilter);

        // Fetch all proposals + computed states concurrently. All proposals
        // on this governor belong to `vault` — no vault filter needed.
        const ids = Array.from({ length: Number(count) }, (_, i) => BigInt(i + 1));
        const results = await Promise.all(
          ids.map(async (id) => {
            const [p, state] = await Promise.all([getProposal(vault, id), getProposalState(vault, id)]);
            return { ...p, computedState: state };
          }),
        );

        const proposals = results.filter((p) => {
          if (stateFilter !== "all" && stateIndex >= 0 && p.computedState !== stateIndex) return false;
          return true;
        });

        spinner.stop();

        if (proposals.length === 0) {
          console.log(DIM("\n  No matching proposals.\n"));
          return;
        }

        console.log();
        console.log(BOLD(`Proposals (${proposals.length})`));
        console.log(DIM("─".repeat(90)));
        console.log(
          DIM("  ID  ") +
          DIM("Agent".padEnd(14)) +
          DIM("State".padEnd(12)) +
          DIM("Fee".padEnd(8)) +
          DIM("Votes (For/Against)".padEnd(22)) +
          DIM("Duration".padEnd(10)) +
          DIM("Created"),
        );
        console.log(DIM("─".repeat(90)));

        for (const p of proposals) {
          const state = PROPOSAL_STATES[p.computedState] || "Unknown";
          const created = p.snapshotTimestamp > 0n
            ? new Date(Number(p.snapshotTimestamp) * 1000).toLocaleDateString()
            : "—";
          const agent = `${p.proposer.slice(0, 6)}...${p.proposer.slice(-4)}`;
          const dur = formatDuration(p.strategyDuration);
          const votes = `${formatShares(p.votesFor)}/${formatShares(p.votesAgainst)}`;
          const fee = `${Number(p.performanceFeeBps) / 100}%`;

          console.log(
            `  ${String(p.id).padEnd(4)}` +
            `${agent.padEnd(14)}` +
            `${state.padEnd(12)}` +
            `${fee.padEnd(8)}` +
            `${votes.padEnd(22)}` +
            `${dur.padEnd(10)}` +
            `${created}`,
          );
        }
        console.log();
      } catch (err) {
        spinner.fail("Failed to load proposals");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }
    });

  // ── proposal show ──

  proposal
    .command("show")
    .description("Show full proposal details")
    .argument("<id>", "Proposal ID")
    .requiredOption("--vault <address>", "Vault the proposal belongs to (per-vault governor)")
    .action(async (idStr, opts) => {
      const spinner = ora("Loading proposal...").start();
      try {
        const vault = parseVaultOption(opts.vault, spinner);
        const id = parseBigIntArg(idStr, "proposal ID");
        // getProposal(out-of-range) returns a zeroed struct (no revert) — guard
        // so we don't render a bogus "Proposal #0 / Expired".
        const count = await proposalCount(vault);
        if (id === 0n || id > count) {
          spinner.fail(`No proposal #${id} on this vault (proposalCount: ${count})`);
          process.exitCode = 1;
          return;
        }
        const p = await getProposal(vault, id);
        const state = await getProposalState(vault, id);
        const params = await getGovernorParams(vault);

        spinner.stop();

        const stateLabel = PROPOSAL_STATES[state] || "Unknown";
        const totalVotes = p.votesFor + p.votesAgainst;
        const quorumNeeded = totalVotes > 0n ? `${Number(params.vetoThresholdBps) / 100}%` : "—";

        console.log();
        console.log(LABEL(`  ◆ Proposal #${p.id}`));
        SEP();

        // Try to resolve IPFS metadata
        if (p.metadataURI && p.metadataURI.startsWith("ipfs://")) {
          try {
            const meta = await fetchMetadata(p.metadataURI);
            console.log(W(`  Name:             ${BOLD(meta.name)}`));
            console.log(W(`  Description:      ${DIM(meta.description)}`));
          } catch {
            console.log(W(`  Metadata:         ${DIM(p.metadataURI)}`));
          }
        } else if (p.metadataURI) {
          console.log(W(`  Metadata:         ${DIM(p.metadataURI)}`));
        }

        console.log(W(`  State:            ${BOLD(stateLabel)}`));
        console.log(W(`  Proposer:         ${G(p.proposer)}`));
        console.log(W(`  Vault:            ${G(p.vault)}`));
        console.log(W(`  Agent Fee:        ${Number(p.performanceFeeBps) / 100}%`));
        console.log(W(`  Duration:         ${formatDuration(p.strategyDuration)}`));

        console.log();
        console.log(LABEL("  Timestamps"));
        console.log(W(`  Snapshot:         ${formatTimestamp(p.snapshotTimestamp)}`));
        console.log(W(`  Vote End:         ${formatTimestamp(p.voteEnd)}`));
        console.log(W(`  Execute By:       ${formatTimestamp(p.executeBy)}`));
        console.log(W(`  Executed At:      ${formatTimestamp(p.executedAt)}`));

        console.log();
        console.log(LABEL("  Votes"));
        console.log(W(`  For:              ${formatShares(p.votesFor)}`));
        console.log(W(`  Against:          ${formatShares(p.votesAgainst)}`));
        console.log(W(`  Abstain:          ${formatShares(p.votesAbstain)}`));
        console.log(W(`  Veto Threshold:   ${quorumNeeded}`));

        if (state === PROPOSAL_STATE.Executed || state === PROPOSAL_STATE.Settled) {
          try {
            const cap = await getCapitalSnapshot(vault, id);
            console.log();
            console.log(LABEL("  Capital"));
            // TODO: formatUSDC hardcodes 6 decimals — should use the vault's actual asset decimals
            console.log(W(`  Snapshot:         ${formatUSDC(cap)}`));
          } catch { /* no snapshot */ }
        }

        const execCalls = await getExecuteCalls(vault, id);
        const settlCalls = await getSettlementCalls(vault, id);

        console.log();
        console.log(LABEL(`  Execute Calls (${execCalls.length})`));
        for (let i = 0; i < execCalls.length; i++) {
          console.log(DIM(`  [${i}] target=${execCalls[i].target}`));
          console.log(DIM(`       data=${execCalls[i].data.slice(0, 20)}...  value=${execCalls[i].value}`));
        }
        console.log(LABEL(`  Settlement Calls (${settlCalls.length})`));
        for (let i = 0; i < settlCalls.length; i++) {
          console.log(DIM(`  [${i}] target=${settlCalls[i].target}`));
          console.log(DIM(`       data=${settlCalls[i].data.slice(0, 20)}...  value=${settlCalls[i].value}`));
        }

        SEP();
        console.log();
      } catch (err) {
        spinner.fail("Failed to load proposal");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }
    });

  // ── proposal vote ──

  proposal
    .command("vote")
    .description("Cast a vote on a pending proposal")
    .requiredOption("--id <proposalId>", "Proposal ID")
    .requiredOption("--vault <address>", "Vault the proposal belongs to (per-vault governor)")
    .requiredOption("--support <for|against|abstain>", "Vote direction: for, against, or abstain")
    .action(async (opts) => {
      try {
        const proposalId = parseBigIntArg(opts.id, "proposal ID");
        const vault = parseVaultOption(opts.vault);
        const supportRaw = String(opts.support).toLowerCase();
        const support = supportRaw === "yes" || supportRaw === "for"
          ? VOTE_TYPE.For
          : supportRaw === "no" || supportRaw === "against"
            ? VOTE_TYPE.Against
            : supportRaw === "abstain"
              ? VOTE_TYPE.Abstain
              : null;
        if (support === null) {
          console.error(chalk.red(`Invalid support value "${opts.support}". Use for|against|abstain.`));
          process.exit(1);
        }

        if (isCalldataOnly()) {
          const voteDir = support === VOTE_TYPE.For ? "For" : support === VOTE_TYPE.Against ? "Against" : "Abstain";
          emitCalldata(
            encodeVote(
              { proposalId, vote: voteDir, governor: await getGovernorOf(vault) },
              getChain().id as ChainId,
            ),
          );
          return;
        }

        const account = getAccount();

        const spinner = ora("Loading proposal...").start();
        const p = await getProposal(vault, proposalId);
        const state = await getProposalState(vault, proposalId);

        if (state !== PROPOSAL_STATE.Pending) {
          spinner.fail(`Proposal is ${PROPOSAL_STATES[state] || "Unknown"}, not Pending`);
          process.exit(1);
        }

        const alreadyVoted = await hasVoted(vault, proposalId, account.address);
        if (alreadyVoted) {
          spinner.fail("You have already voted on this proposal");
          process.exit(1);
        }

        const weight = await getVoteWeight(vault, proposalId, account.address);
        spinner.stop();

        console.log();
        console.log(LABEL("  ◆ Cast Vote"));
        SEP();
        console.log(W(`  Proposal:  #${proposalId}`));
        console.log(W(`  Vault:     ${G(p.vault)}`));
        console.log(W(
          `  Support:   ${
            support === VOTE_TYPE.For ? G("FOR")
              : support === VOTE_TYPE.Against ? chalk.red("AGAINST")
                : DIM("ABSTAIN")
          }`,
        ));
        console.log(W(`  Weight:    ${formatShares(weight)} shares`));
        SEP();

        const voteSpinner = ora({ text: W("Submitting vote..."), color: "green" }).start();
        const hash = await vote(vault, proposalId, support);
        voteSpinner.succeed(G("Vote cast"));
        console.log(DIM(`  ${getExplorerUrl(hash)}`));
        console.log();
      } catch (err) {
        console.error(chalk.red(`\n  ✖ ${formatContractError(err)}`));
        process.exit(1);
      }
    });

  // NOTE: `proposal bind-adapter` was removed. The strategy address is now
  // a `propose()` parameter (`strategy` field on `StrategyProposal`), set
  // immutably at propose time — no separate bind step. `strategy propose`
  // resolves the freshly-cloned strategy address and passes it through.

  // ── proposal execute ──

  proposal
    .command("execute")
    .description("Execute an approved proposal")
    .requiredOption("--id <proposalId>", "Proposal ID")
    .requiredOption("--vault <address>", "Vault the proposal belongs to (per-vault governor)")
    .option("--dry-run", "Simulate execution without sending a transaction")
    .action(async (opts) => {
      try {
        const proposalId = parseBigIntArg(opts.id, "proposal ID");
        const vault = parseVaultOption(opts.vault);

        if (isCalldataOnly()) {
          emitCalldata(
            encodeExecute(
              { proposalId, governor: await getGovernorOf(vault) },
              getChain().id as ChainId,
            ),
          );
          return;
        }

        const spinner = ora("Loading proposal...").start();
        // Fetch the view state, the proposal struct (for reviewEnd/executeBy),
        // and chain time together. The gate must mirror the contract, which
        // self-resolves an elapsed cold-start GuardianReview inside
        // executeProposal — see evaluateExecuteGate. `now` is chain time
        // (latest block timestamp), the value block.timestamp compares against.
        const [state, p, latestBlock] = await Promise.all([
          getProposalState(vault, proposalId),
          getProposal(vault, proposalId),
          getPublicClient().getBlock(),
        ]);
        const now = latestBlock.timestamp;
        const gate = evaluateExecuteGate({
          state,
          reviewEnd: p.reviewEnd,
          executeBy: p.executeBy,
          now,
        });

        if (gate.kind === "refuse-review-open") {
          spinner.fail(
            `Proposal is in GuardianReview — the review window is open until ${formatTimestamp(p.reviewEnd)}. ` +
              `Execute once the window closes; the execute self-resolves the review.`,
          );
          process.exit(1);
        }
        if (gate.kind === "refuse-expired") {
          spinner.fail(
            `Proposal expired — the execution window closed at ${formatTimestamp(p.executeBy)} (execute-by). ` +
              `An elapsed GuardianReview past execute-by resolves to Expired and can no longer be executed.`,
          );
          process.exit(1);
        }
        if (gate.kind === "refuse-state") {
          spinner.fail(`Proposal is ${PROPOSAL_STATES[state] || "Unknown"}, not Approved`);
          process.exit(1);
        }
        // gate.kind is "send" (Approved) or "send-self-resolve" (elapsed
        // cold-start GuardianReview — executeProposal resolves it inline).

        // Dry run — simulate instead of executing
        if (opts.dryRun) {
          const execCalls = await getExecuteCalls(vault, proposalId);
          const dryRunContext: RiskContext = {
            vault: p.vault,
            performanceFeeBps: p.performanceFeeBps,
            strategyDuration: p.strategyDuration,
          };
          spinner.text = W("Simulating execution...");
          const result = await simulateBatchCalls(p.vault, execCalls, "execute", dryRunContext);
          spinner.stop();
          printSimulationResult(result, "execute");
          const hasCritical = result.risks.some((r) => r.level === "critical");
          process.exit(result.success && !hasCritical ? 0 : 1);
        }

        if (gate.kind === "send-self-resolve") {
          // `.info()` stops the spinner and persists the note; restart it for
          // the execute step so `.succeed()` renders cleanly below.
          spinner.info(
            DIM(
              "GuardianReview window elapsed with no cached resolution — execute will " +
                "self-resolve the review inline (cold-start / not blocked) and proceed.",
            ),
          );
          spinner.start(W("Executing proposal..."));
        } else {
          spinner.text = W("Executing proposal...");
        }
        const hash = await executeProposal(vault, proposalId);
        spinner.succeed(G("Proposal executed"));

        console.log(DIM(`  ${getExplorerUrl(hash)}`));

        try {
          const cap = await getCapitalSnapshot(vault, proposalId);
          // TODO: formatUSDC hardcodes 6 decimals — should use the vault's actual asset decimals
          console.log(DIM(`  Capital snapshot: ${formatUSDC(cap)}`));
        } catch { /* no snapshot yet */ }

        console.log();
      } catch (err) {
        console.error(chalk.red(`\n  ✖ ${formatContractError(err)}`));
        process.exit(1);
      }
    });

  // ── proposal settle ──

  proposal
    .command("settle")
    .description("Settle an executed proposal (auto-routes settlement path)")
    .requiredOption("--id <proposalId>", "Proposal ID")
    .requiredOption("--vault <address>", "Vault the proposal belongs to (per-vault governor)")
    .option("--calls <path>", "Path to JSON file with settle Call[] (for agent/emergency settle)")
    .action(async (opts) => {
      try {
        const proposalId = parseBigIntArg(opts.id, "proposal ID");
        const vault = parseVaultOption(opts.vault);

        if (isCalldataOnly()) {
          const governor = await getGovernorOf(vault);
          // With --calls this is an owner-opened emergency-settle review
          // (emergencySettleWithCalls); without, the standard settle path.
          if (opts.calls) {
            const calls = parseCallsFile(opts.calls);
            emitCalldata(
              encodeEmergencySettle({ proposalId, calls, governor }, getChain().id as ChainId),
            );
          } else {
            emitCalldata(encodeSettle({ proposalId, governor }, getChain().id as ChainId));
          }
          return;
        }

        const account = getAccount();

        const spinner = ora("Loading proposal...").start();
        // `durationElapsed` gates the permissionless (non-proposer) settle path,
        // which the contract allows once `block.timestamp >= executedAt +
        // strategyDuration`. Compare against CHAIN time (latest block
        // timestamp), not wall-clock `Date.now()` — on a time-warped vnet the
        // two diverge and wall-clock would wrongly refuse a keeper settle the
        // contract accepts (same fix as resolve-reviews).
        const [p, state, latestBlock] = await Promise.all([
          getProposal(vault, proposalId),
          getProposalState(vault, proposalId),
          getPublicClient().getBlock(),
        ]);

        if (state !== PROPOSAL_STATE.Executed) {
          spinner.fail(`Proposal is ${PROPOSAL_STATES[state] || "Unknown"}, not Executed`);
          process.exit(1);
        }

        const isProposer = account.address.toLowerCase() === p.proposer.toLowerCase();
        const now = latestBlock.timestamp;
        const durationElapsed = p.executedAt > 0n && now >= p.executedAt + p.strategyDuration;

        let hash: Hex;

        if (isProposer && !durationElapsed) {
          // Proposer self-settle. The contract lets the proposer settle after a
          // fixed floor (MIN_STRATEGY_DURATION_BEFORE_SELF_SETTLE = 1h), NOT
          // literally anytime — a settle inside that first hour reverts
          // StrategyDurationNotElapsed. Left un-gated here (over-permissive):
          // the on-chain revert is clear and this path is not the #452 class
          // (wrongly BLOCKING an allowed action).
          spinner.text = W("Settling (proposer)...");
          hash = await settleProposal(vault, proposalId);
          spinner.succeed(G("Settled by proposer"));
        } else if (durationElapsed && !opts.calls) {
          // Permissionless settle after duration
          spinner.text = W("Settling (permissionless)...");
          hash = await settleProposal(vault, proposalId);
          spinner.succeed(G("Settled (permissionless)"));
        } else if (durationElapsed && opts.calls) {
          // Emergency settle — owner with custom calls
          spinner.text = W("Emergency settling...");
          const calls = parseCallsFile(opts.calls);
          hash = await emergencySettle(vault, proposalId, calls);
          spinner.succeed(G("Emergency settled"));
        } else {
          spinner.fail("Cannot settle: duration not elapsed and you are not the proposer.");
          process.exit(1);
        }

        console.log(DIM(`  ${getExplorerUrl(hash)}`));
        console.log();
      } catch (err) {
        console.error(chalk.red(`\n  ✖ ${formatContractError(err)}`));
        process.exit(1);
      }
    });

  // ── proposal unstick ──
  // Guardian-only recovery path. Hidden from `--help` on purpose: this is a
  // last-resort command used by the fund-owner skill when an Executed
  // proposal's pre-committed settlement calls revert, leaving the vault
  // locked (`redemptionsLocked() == true`, `getActiveProposal(vault) != 0`).
  //
  // It calls `emergencySettle(id, [noopCall])` where `noopCall` is a
  // harmless `asset.balanceOf(vault)` view-call on the vault's underlying
  // asset. The governor's `_tryPrecommittedThenFallback` catches the
  // pre-committed revert and runs this no-op fallback, causing
  // `_finishSettlement` to mark the proposal `Settled` and clear
  // `_activeProposal[vault]`. LPs can redeem afterwards.
  //
  // Docs: skill/skills/fund-owner/SKILL.md § "Recovering a stuck proposal"

  proposal
    .command("unstick", { hidden: true })
    .description(
      "Guardian-only: unstick a vault whose Executed proposal cannot settle (no funds moved)",
    )
    .requiredOption("--id <proposalId>", "Proposal ID to unstick")
    .requiredOption("--vault <address>", "Vault the proposal belongs to (per-vault governor)")
    .option("--yes", "Skip the interactive confirmation")
    .option(
      "--dry-run",
      "Check preconditions and print the fallback call without broadcasting",
    )
    .action(async (opts) => {
      try {
        const proposalId = parseBigIntArg(opts.id, "proposal ID");
        const vault = parseVaultOption(opts.vault);

        // --calldata-only: emit the V1.5 unstick(proposalId) call. The contract
        // enforces the owner / Executed / duration-elapsed preconditions, so we
        // skip the (account-requiring) pre-flight checks below.
        if (isCalldataOnly()) {
          emitCalldata(
            encodeUnstick(
              { proposalId, governor: await getGovernorOf(vault) },
              getChain().id as ChainId,
            ),
          );
          return;
        }

        const account = getAccount();

        const spinner = ora("Loading proposal...").start();
        const p = await getProposal(vault, proposalId);
        const state = await getProposalState(vault, proposalId);

        // 1. State must be Executed
        if (state !== PROPOSAL_STATE.Executed) {
          spinner.fail(
            `Proposal ${proposalId} is ${PROPOSAL_STATES[state] || "Unknown"} — unstick only applies to Executed proposals.`,
          );
          console.error(
            DIM(
              "\n  Other states have their own recovery path:\n" +
                "    Draft/Pending/GuardianReview/Approved → sherwood proposal cancel --id <id> (proposer)\n" +
                "    Draft/Pending (vault owner)           → sherwood proposal cancel --id <id> --emergency\n" +
                "    Pending (vault owner)                 → sherwood proposal veto --id <id>\n" +
                "    Expired          → vault is not locked, nothing to do\n" +
                "    Settled          → already settled\n" +
                "    Cancelled        → governor upgrade required (issue #177)\n",
            ),
          );
          process.exit(1);
        }

        // 2. Strategy duration must have elapsed (emergencySettle requires it)
        const now = BigInt(Math.floor(Date.now() / 1000));
        const expiresAt = p.executedAt + p.strategyDuration;
        if (p.executedAt === 0n || now < expiresAt) {
          spinner.fail("Strategy duration has not elapsed yet.");
          const remaining = expiresAt - now;
          console.error(
            DIM(
              `\n  Can unstick after: ${new Date(Number(expiresAt) * 1000).toLocaleString()} (in ~${formatDuration(remaining)})\n`,
            ),
          );
          process.exit(1);
        }

        // 3. Vault must actually be locked by this proposal
        const active = await getActiveProposal(vault);
        if (active !== proposalId) {
          spinner.fail(
            `Vault ${vault} active proposal is ${active}, not ${proposalId} — nothing to unstick for this proposal.`,
          );
          process.exit(1);
        }

        // 4. Caller must be vault owner
        const vaultOwner = await getVaultOwner(vault);
        if (account.address.toLowerCase() !== vaultOwner.toLowerCase()) {
          spinner.fail("Only the vault owner can unstick a proposal.");
          console.error(
            DIM(`\n  Vault owner: ${vaultOwner}\n  Your wallet: ${account.address}\n`),
          );
          process.exit(1);
        }

        // 5. Craft the no-op fallback call: asset.balanceOf(vault)
        // Cannot revert, costs ~2.5k gas, satisfies fallbackCalls.length > 0
        // requirement in _tryPrecommittedThenFallback.
        const asset = await getAssetAddress();
        const noopCalldata = encodeFunctionData({
          abi: ERC20_ABI,
          functionName: "balanceOf",
          args: [vault],
        });
        const fallbackCalls: BatchCall[] = [
          { target: asset, data: noopCalldata, value: 0n },
        ];

        spinner.stop();

        // Print plan
        console.log();
        console.log(BOLD("Unstick plan"));
        SEP();
        console.log(`  Proposal:     ${proposalId}`);
        console.log(`  Vault:        ${vault}`);
        console.log(`  Asset:        ${asset}`);
        console.log(`  State:        ${PROPOSAL_STATES[state]} (duration elapsed)`);
        console.log(`  Caller:       ${account.address} (vault owner ✓)`);
        console.log();
        console.log(LABEL("  Fallback call (no-op, cannot revert):"));
        console.log(DIM(`    target: ${asset}`));
        console.log(DIM(`    data:   ${noopCalldata}  // balanceOf(${vault})`));
        console.log(DIM("    value:  0"));
        console.log();
        console.log(
          DIM(
            "  This calls emergencySettle(id, [noop]). The governor catches any revert\n" +
              "  from the pre-committed settlement calls and runs the no-op instead. No\n" +
              "  funds move. The proposal transitions to Settled and the vault unlocks.\n" +
              "  LPs can then redeem via `sherwood vault redeem`.",
          ),
        );
        console.log();

        if (opts.dryRun) {
          console.log(G("  (dry-run) Preconditions OK — not broadcasting.\n"));
          return;
        }

        if (!opts.yes) {
          process.stdout.write(chalk.yellow("  Proceed? [y/N] "));
          const answer = await new Promise<string>((resolve) => {
            process.stdin.resume();
            process.stdin.setEncoding("utf-8");
            process.stdin.once("data", (d) => resolve(String(d).trim().toLowerCase()));
          });
          process.stdin.pause();
          if (answer !== "y" && answer !== "yes") {
            console.log(DIM("  Cancelled.\n"));
            return;
          }
        }

        const send = ora("Broadcasting emergencySettle...").start();
        const hash = await emergencySettle(vault, proposalId, fallbackCalls);
        send.succeed(G("Vault unstuck — proposal Settled"));
        console.log(DIM(`  ${getExplorerUrl(hash)}`));

        // Verify unlock
        const stillActive = await getActiveProposal(vault);
        if (stillActive === 0n) {
          console.log(G("  redemptionsLocked cleared ✓"));
        } else {
          console.log(
            chalk.yellow(
              `  ⚠ getActiveProposal(vault) still returns ${stillActive} — check manually.`,
            ),
          );
        }
        console.log();
      } catch (err) {
        console.error(chalk.red(`\n  ✖ ${formatContractError(err)}`));
        process.exit(1);
      }
    });

  // ── proposal resolve-reviews ──
  //
  // Keeper command. `GuardianRegistry.resolveReview(governor, proposalId)` is
  // permissionless and timestamp-gated: a review can only be resolved once
  // `block.timestamp >= reviewEnd`. For an OPENED review, resolution computes
  // the block quorum and triggers the approver slash — if nobody calls it, the
  // slash never lands. That is the only case that needs a keeper tx here.
  //
  // A zero-guardian / never-opened review (`getReviewState().opened == false`)
  // is the cold-start norm in the wild (`openReview` is production-gated on a
  // guardian cohort). `resolveReview` on it is a no-op that only marks
  // resolved/not-blocked (GuardianRegistry.sol:639-643) — and
  // `executeProposal` already self-resolves it inline (issue #452). So this
  // keeper no longer spends a redundant tx on cold-start reviews: it reports
  // them as directly executable via `proposal execute` instead. Enumerates
  // proposals via the governor (`proposalCount` + `getProposal`, same pattern
  // as `proposal list`).

  proposal
    .command("resolve-reviews")
    .description("Keeper: resolve guardian reviews whose review window has elapsed")
    .requiredOption("--vault <address>", "Vault whose governor's reviews to scan (per-vault governor)")
    .option("--dry-run", "List resolvable reviews without sending transactions")
    .action(async (opts) => {
      const spinner = ora("Scanning proposals for resolvable reviews...").start();
      try {
        const vault = parseVaultOption(opts.vault, spinner);
        const registryAddr = getRegistryAddress();
        const pc = getPublicClient();

        // Registry reviews are keyed (governor, proposalId) post per-vault split.
        const governorAddr = await getGovernorOf(vault);

        const count = await proposalCount(vault);
        if (count === 0n) {
          spinner.stop();
          console.log(DIM("\n  No proposals — nothing to resolve.\n"));
          return;
        }

        // `reviewEnd` is chain time — compare against the chain's latest block
        // timestamp, not wall-clock `Date.now()`. On a time-warped vnet the two
        // diverge (block.timestamp is warped forward, wall-clock isn't), so
        // Date.now() would report "nothing to resolve" and skip the tx. Reading
        // the block timestamp is correct on every chain.
        const now = (await pc.getBlock()).timestamp;
        const ids = Array.from({ length: Number(count) }, (_, i) => BigInt(i + 1));

        // For each proposal: fetch its governor view (for `reviewEnd`) and the
        // registry review state, then classify (see `classifyReview`):
        //   - `resolvable`: OPENED + unresolved + past window → needs a real
        //     `resolveReview` keeper tx (block quorum, approver slash).
        //   - `cold-start`: UNOPENED + unresolved + past window → the
        //     zero-guardian norm; `resolveReview` is a redundant no-op the
        //     `executeProposal` self-resolve already handles. Report + point at
        //     `proposal execute` rather than spend a tx.
        const candidates = await Promise.all(
          ids.map(async (id) => {
            try {
              const [p, reviewState] = await Promise.all([
                getProposal(vault, id),
                pc.readContract({
                  address: registryAddr,
                  abi: GUARDIAN_REGISTRY_ABI,
                  functionName: "getReviewState",
                  args: [governorAddr, id],
                }) as Promise<readonly [boolean, boolean, boolean, boolean]>,
              ]);
              const [opened, resolved] = reviewState;
              const cls = classifyReview({ opened, resolved, reviewEnd: p.reviewEnd, now });
              return { id, cls, reviewEnd: p.reviewEnd, vault: p.vault };
            } catch {
              // A proposal that fails to load is skipped, not fatal — the
              // keeper should still resolve every other candidate.
              return { id, cls: "none" as const, reviewEnd: 0n, vault: "0x" as Address };
            }
          }),
        );

        const resolvable = candidates.filter((c) => c.cls === "resolvable");
        const coldStart = candidates.filter((c) => c.cls === "cold-start");
        spinner.stop();

        // Cold-start (zero-guardian) reviews: nothing to resolve on-chain —
        // surface them so the operator knows they are directly executable
        // rather than reporting a misleading "Resolved 0/N" (issue #452).
        if (coldStart.length > 0) {
          console.log();
          console.log(LABEL(`  ◆ Unopened Cold-Start Reviews (${coldStart.length})`));
          SEP();
          for (const c of coldStart) {
            console.log(
              W(`  Proposal #${c.id}  vault=${c.vault}  reviewEnd=${formatTimestamp(c.reviewEnd)}`),
            );
            console.log(
              DIM(
                `    No guardian review was opened (zero-guardian cold start) — nothing to ` +
                  `resolve on-chain.\n    Directly executable: sherwood proposal execute --id ${c.id} --vault ${c.vault}\n` +
                  `    (execute self-resolves the review inline).`,
              ),
            );
          }
          SEP();
        }

        if (resolvable.length === 0) {
          if (coldStart.length === 0) {
            console.log(DIM("\n  No reviews ready to resolve.\n"));
          } else {
            console.log(DIM("\n  No opened reviews need an on-chain resolve.\n"));
          }
          return;
        }

        console.log();
        console.log(LABEL(`  ◆ Resolvable Guardian Reviews (${resolvable.length})`));
        SEP();
        for (const c of resolvable) {
          console.log(
            W(`  Proposal #${c.id}  vault=${c.vault}  reviewEnd=${formatTimestamp(c.reviewEnd)}`),
          );
        }
        SEP();

        if (opts.dryRun) {
          console.log(G("\n  (dry-run) Not broadcasting. Re-run without --dry-run to resolve.\n"));
          return;
        }

        // Resolve each — `resolveReview` is a state-changing tx (signing
        // wallet required). Sequential to keep nonces ordered.
        const resolved: { id: bigint; blocked: boolean; hash: Hex }[] = [];
        for (const c of resolvable) {
          const txSpinner = ora(`Resolving review for proposal #${c.id}...`).start();
          try {
            // Simulate first to read the `blocked` return value before sending.
            const { result: blocked } = await pc.simulateContract({
              address: registryAddr,
              abi: GUARDIAN_REGISTRY_ABI,
              functionName: "resolveReview",
              args: [governorAddr, c.id],
              account: getAccount(),
            });
            const hash = await writeContractWithRetry({
              account: getAccount(),
              chain: getChain(),
              address: registryAddr,
              abi: GUARDIAN_REGISTRY_ABI,
              functionName: "resolveReview",
              args: [governorAddr, c.id],
            });
            await waitForReceipt(hash);
            resolved.push({ id: c.id, blocked: blocked as boolean, hash });
            txSpinner.succeed(
              G(`Proposal #${c.id} resolved — blocked=${blocked ? chalk.red("true") : "false"}`),
            );
          } catch (err) {
            txSpinner.fail(`Proposal #${c.id} resolve failed: ${formatContractError(err)}`);
          }
        }

        console.log();
        console.log(LABEL(`  ◆ Resolved ${resolved.length}/${resolvable.length}`));
        SEP();
        for (const r of resolved) {
          console.log(
            W(`  Proposal #${r.id}  blocked=${r.blocked ? chalk.red("true") : "false"}  `) +
              DIM(getExplorerUrl(r.hash)),
          );
        }
        SEP();
        console.log();

        if (resolved.length < resolvable.length) process.exit(1);
      } catch (err) {
        spinner.fail("Failed to scan reviews");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }
    });

  // ── proposal cancel ──

  proposal
    .command("cancel")
    .description("Cancel a proposal (proposer or vault owner)")
    .requiredOption("--id <proposalId>", "Proposal ID")
    .requiredOption("--vault <address>", "Vault the proposal belongs to (per-vault governor)")
    .option("--emergency", "Emergency cancel (vault owner only, any non-settled state)")
    .action(async (opts) => {
      try {
        const proposalId = parseBigIntArg(opts.id, "proposal ID");
        const vault = parseVaultOption(opts.vault);

        if (isCalldataOnly()) {
          emitCalldata(
            encodeCancel(
              { proposalId, governor: await getGovernorOf(vault) },
              getChain().id as ChainId,
            ),
          );
          return;
        }

        const spinner = ora("Loading proposal...").start();
        const state = await getProposalState(vault, proposalId);

        if (state === PROPOSAL_STATE.Settled || state === PROPOSAL_STATE.Cancelled) {
          spinner.fail(`Proposal is already ${PROPOSAL_STATES[state]}`);
          process.exit(1);
        }

        // Allowlists mirror SyndicateGovernor.cancelProposal (proposer path,
        // Draft / Pending / GuardianReview / Approved) and emergencyCancel
        // (vault-owner path, Draft / Pending only). Earlier versions of this
        // CLI rejected proposer-side cancels from GuardianReview / Approved
        // with the misleading hint "use --emergency for non-pending/approved"
        // — but emergencyCancel reverts on those states. Keep these in sync
        // with the contract; treating the on-chain revert as the only gate
        // gives a worse error than this pre-check does.
        const PROPOSER_CANCELLABLE: ReadonlySet<number> = new Set([
          PROPOSAL_STATE.Draft,
          PROPOSAL_STATE.Pending,
          PROPOSAL_STATE.GuardianReview,
          PROPOSAL_STATE.Approved,
        ]);
        const OWNER_EMERGENCY_CANCELLABLE: ReadonlySet<number> = new Set([
          PROPOSAL_STATE.Draft,
          PROPOSAL_STATE.Pending,
        ]);

        let hash: Hex;

        if (opts.emergency) {
          if (!OWNER_EMERGENCY_CANCELLABLE.has(state)) {
            spinner.fail(
              `Proposal is ${PROPOSAL_STATES[state] || "Unknown"} — emergencyCancel only works in Draft or Pending. ` +
                `For GuardianReview / Approved, the proposer can run: sherwood proposal cancel --id ${proposalId}`,
            );
            process.exit(1);
          }
          spinner.text = W("Emergency cancelling...");
          hash = await emergencyCancel(vault, proposalId);
          spinner.succeed(G("Emergency cancelled"));
        } else {
          if (!PROPOSER_CANCELLABLE.has(state)) {
            spinner.fail(
              `Proposal is ${PROPOSAL_STATES[state] || "Unknown"} — not cancellable in this state. ` +
                `cancelProposal accepts Draft / Pending / GuardianReview / Approved (proposer only).`,
            );
            process.exit(1);
          }
          spinner.text = W("Cancelling proposal...");
          hash = await cancelProposal(vault, proposalId);
          spinner.succeed(G("Proposal cancelled"));
        }

        console.log(DIM(`  ${getExplorerUrl(hash)}`));
        console.log();
      } catch (err) {
        console.error(chalk.red(`\n  ✖ ${formatContractError(err)}`));
        process.exit(1);
      }
    });

  // ── proposal simulate ──

  proposal
    .command("simulate")
    .description("Simulate proposal calls via Tenderly (or eth_call fallback)")
    .option("--id <proposalId>", "Proposal ID to simulate")
    .option("--vault <address>", "Vault address (required — proposal IDs are per-vault)")
    .option("--execute-calls <path>", "Path to JSON execute calls file")
    .option("--settle-calls <path>", "Path to JSON settlement calls file")
    .option("--notify <subdomain>", "Send results to fund XMTP chat")
    .action(async (opts) => {
      try {
        let vault: Address;
        let executeCalls: BatchCall[];
        let settlementCalls: BatchCall[] = [];
        let riskContext: RiskContext = {};
        let proposalId: bigint | undefined;

        if (opts.id) {
          // By proposal ID — fetch calls and build full risk context.
          // Per-vault governor: the vault is required to resolve which
          // governor holds the proposal.
          if (!opts.vault || !isAddress(opts.vault)) {
            console.error(chalk.red("\n  ✖ --vault is required when using --id (proposal IDs are per-vault)"));
            process.exit(1);
          }
          vault = opts.vault as Address;
          proposalId = parseBigIntArg(opts.id, "proposal ID");
          const spinner = ora("Loading proposal...").start();

          const p = await getProposal(vault, proposalId);
          executeCalls = await getExecuteCalls(vault, proposalId);
          settlementCalls = await getSettlementCalls(vault, proposalId);

          riskContext = {
            vault,
            performanceFeeBps: p.performanceFeeBps,
            strategyDuration: p.strategyDuration,
          };

          try {
            const govParams = await getGovernorParams(vault);
            riskContext.governorParams = govParams;
          } catch { /* non-fatal */ }

          spinner.stop();
          console.log(DIM(`\n  Simulating proposal #${proposalId} on vault ${vault.slice(0, 10)}...`));
        } else if (opts.executeCalls) {
          // By call files — minimal risk context (no proposal params)
          if (!opts.vault || !isAddress(opts.vault)) {
            console.error(chalk.red("\n  ✖ --vault is required when using --execute-calls"));
            process.exit(1);
          }
          vault = opts.vault as Address;
          executeCalls = parseCallsFile(opts.executeCalls);
          if (opts.settleCalls) {
            settlementCalls = parseCallsFile(opts.settleCalls);
          }
          riskContext = { vault };
        } else {
          console.error(chalk.red("\n  ✖ Provide --id <proposalId> or --execute-calls <path>"));
          process.exit(1);
        }

        // Simulate execute calls
        const spinner = ora({ text: W("Simulating execute calls..."), color: "green" }).start();
        const execResult = await simulateBatchCalls(vault, executeCalls, "execute", riskContext);
        spinner.stop();
        printSimulationResult(execResult, "execute");

        // Simulate settlement calls if present
        let settleResult;
        if (settlementCalls.length > 0) {
          const settleSpinner = ora({ text: W("Simulating settlement calls..."), color: "green" }).start();
          settleResult = await simulateBatchCalls(vault, settlementCalls, "settlement", riskContext);
          settleSpinner.stop();
          printSimulationResult(settleResult, "settlement");
        }

        // Send XMTP notification if requested
        if (opts.notify && proposalId !== undefined) {
          const notifySpinner = ora({ text: W("Sending XMTP alert..."), color: "green" }).start();
          await sendSimulationAlert(opts.notify, proposalId, vault, execResult, settleResult);
          notifySpinner.succeed(G("Risk report sent to chat"));
        }

        // Exit with failure code if simulation or risks are critical
        const hasCritical = execResult.risks.some((r) => r.level === "critical")
          || (settleResult?.risks.some((r) => r.level === "critical") ?? false);

        if (!execResult.success || (settleResult && !settleResult.success) || hasCritical) {
          process.exit(1);
        }
      } catch (err) {
        console.error(chalk.red(`\n  ✖ ${err instanceof Error ? err.message : String(err)}`));
        process.exit(1);
      }
    });
}
